import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import MatureContentGate from '../components/MatureContentGate'
import ReportModal from '../components/ReportModal'
import TipModal from '../components/TipModal'
import { useAuth } from '../context/AuthContext'
import { useLike } from '../hooks/useLike'
import { useBlocks } from '../hooks/useBlocks'
import SkeletonLoader from '../components/SkeletonLoader'
import BackToTop from '../components/BackToTop'
import { sanitizeBio, sanitizeStoryContent } from '../utils/renderBio'
import { decodeEntities } from '../lib/decodeEntities'
import SEO from '../components/SEO'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COVER_GRADIENTS = [
  'linear-gradient(135deg, #1E0A3C 0%, #3B0764 35%, #831843 100%)',
  'linear-gradient(135deg, #0F4C75 0%, #1E0A3C 50%, #3B0764 100%)',
  'linear-gradient(135deg, #064E3B 0%, #0F4C75 50%, #1E0A3C 100%)',
  'linear-gradient(135deg, #3B0764 0%, #831843 50%, #1D4ED8 100%)',
  'linear-gradient(135deg, #831843 0%, #1E0A3C 50%, #064E3B 100%)',
  'linear-gradient(135deg, #1D4ED8 0%, #1E0A3C 50%, #831843 100%)',
]

const AVATAR_COLORS = [
  'linear-gradient(135deg, #7C3AED, #EC4899)',
  'linear-gradient(135deg, #0D9488, #1D4ED8)',
  'linear-gradient(135deg, #EA580C, #EC4899)',
  'linear-gradient(135deg, #059669, #0D9488)',
  'linear-gradient(135deg, #BE123C, #7C3AED)',
  'linear-gradient(135deg, #1D4ED8, #14B8A6)',
]

const STATUS_STYLE = {
  Ongoing:  { bg: '#ECFDF5', color: '#059669' },
  Complete: { bg: '#EFF6FF', color: '#1D4ED8' },
  Hiatus:   { bg: '#FFF7ED', color: '#EA580C' },
}

const RATING_STYLE = {
  General: { bg: '#F0FDF4', color: '#16A34A' },
  Teen:    { bg: '#EFF6FF', color: '#2563EB' },
  Mature:  { bg: '#FFF7ED', color: '#EA580C' },
}

function formatNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function StoryReader() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const explicitChapter = searchParams.get('chapter')
  const savedChapter = explicitChapter ? null : (() => { try { return parseInt(localStorage.getItem(`verse_reading_${id}`) ?? '1', 10) } catch { return 1 } })()
  const chapterNum = Math.max(1, parseInt(explicitChapter ?? savedChapter ?? '1', 10))
  const { user, profile: authProfile, getToken } = useAuth()

  const [story, setStory]               = useState(null)
  const [chapters, setChapters]         = useState([])
  const [loadError, setLoadError]       = useState(false)
  const { liked, count: likeCount, toggle: toggleLike } = useLike('story', story?.id, story?.likes)
  const [bookmarked, setBookmarked]     = useState(false)
  const [bookmarkBusy, setBookmarkBusy] = useState(false)
  const [fontSize, setFontSize]         = useState('md')
  const [readerTheme, setReaderTheme]   = useState(() => {
    try { return localStorage.getItem('verse_reader_theme') ?? 'light' } catch { return 'light' }
  })
  const [reportOpen, setReportOpen]     = useState(false)
  const [copied, setCopied]             = useState(false)
  const [progress, setProgress]         = useState(0)
  const [comment, setComment]           = useState('')
  const [comments, setComments]         = useState([])
  const [matureConfirmed, setMatureConfirmed] = useState(false)
  const [deleteConfirm, setDeleteConfirm]   = useState(false)
  const [deleting, setDeleting]             = useState(false)
  const [tipModalOpen, setTipModalOpen]     = useState(false)
  const [exporting, setExporting]           = useState(false)
  const [exportOpen, setExportOpen]         = useState(false)
  const [shareOpen, setShareOpen]           = useState(false)
  const [chapterReportOpen, setChapterReportOpen] = useState(false)
  const [hideBusy, setHideBusy] = useState(false)
  const exportRef = useRef(null)
  const shareRef = useRef(null)
  const contentRef = useRef(null)
  const articleRef = useRef(null)
  const touchRef = useRef({ startX: 0, startY: 0 })

  useEffect(() => {
    let cancelled = false
    setStory(null)
    setChapters([])
    setLoadError(false)
    async function load() {
      try {
        const token = await getToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        // Fetch story first so we can handle the mature gate (which returns 401)
        let storyRes
        try {
          storyRes = await axios.get(`/api/stories/${id}`, { headers })
        } catch (err) {
          const status = err?.response?.status
          const code = err?.response?.data?.code
          if (status === 401 && code === 'AUTH_REQUIRED_MATURE') {
            const from = window.location.pathname + window.location.search
            navigate('/signup', { state: { from }, replace: true })
            return
          }
          throw err
        }
        if (cancelled) return
        setStory(storyRes.data)

        // Now fetch chapters separately
        const chaptersRes = await axios.get(`/api/stories/${id}/chapters`, { headers })
        if (cancelled) return
        setChapters(chaptersRes.data ?? [])

        // Record a view once per session per story
        const viewedKey = 'verse_viewed'
        const viewed = JSON.parse(sessionStorage.getItem(viewedKey) ?? '[]')
        if (!viewed.includes(id)) {
          sessionStorage.setItem(viewedKey, JSON.stringify([...viewed, id]))
          axios.post(`/api/stories/${id}/view`, {}, { headers })
            .then(r => { if (!cancelled) setStory(s => s ? { ...s, views: r.data.views } : s) })
            .catch(() => {})
        }
      } catch (err) {
        if (cancelled) return
        console.error('[StoryReader] load story failed:', err)
        setLoadError(true)
      }
    }
    load()
    return () => { cancelled = true }
  }, [id])

  // Load comments once story is ready
  useEffect(() => {
    if (!story) return
    let cancelled = false
    getToken().then(token => {
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      axios.get(`/api/comments/${story.id}`, { headers })
        .then(({ data }) => {
          if (cancelled) return
          setComments(data)
          setTimeout(() => {
            const hash = window.location.hash
            if (hash) {
              const el = document.getElementById(hash.slice(1))
              if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('highlight-flash') }
            }
          }, 600)
        })
        .catch(err => { console.warn('[story-comments] fetch failed', err) })
    })
    return () => { cancelled = true }
  }, [story?.id])

  useEffect(() => {
    function onNotificationNavigate() {
      const hash = window.location.hash
      if (!hash) return
      const el = document.getElementById(hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('highlight-flash')
      }
    }
    window.addEventListener('verse:notification-navigate', onNotificationNavigate)
    return () => window.removeEventListener('verse:notification-navigate', onNotificationNavigate)
  }, [])

  // Load bookmark status once story + user are ready
  useEffect(() => {
    if (!story || !user) return
    getToken().then(token => {
      if (!token) return
      const headers = { Authorization: `Bearer ${token}` }
      axios.get(`/api/profile/bookmarks/check/${story.id}`, { headers })
        .then(({ data }) => setBookmarked(data.bookmarked))
        .catch(err => console.error('[StoryReader] Failed to check story bookmark state:', err))
    })
  }, [story?.id, user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleBookmark() {
    if (!user || bookmarkBusy) return
    if (navigator.vibrate) navigator.vibrate(10)
    setBookmarkBusy(true)
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      if (bookmarked) {
        await axios.delete(`/api/profile/bookmarks/${story.id}`, { headers })
        setBookmarked(false)
      } else {
        await axios.post(`/api/profile/bookmarks/${story.id}`, {}, { headers })
        setBookmarked(true)
      }
    } catch (err) { console.error('[StoryReader] toggle bookmark failed:', err) }
    finally { setBookmarkBusy(false) }
  }

  async function handleDelete() {
    if (!deleteConfirm) { setDeleteConfirm(true); return }
    setDeleting(true)
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      await axios.delete(`/api/stories/${story.id}`, { headers })
      navigate(`/user/${story.authorUsername ?? story.author}`)
    } catch (err) { console.error('[StoryReader] delete story failed:', err); setDeleting(false); setDeleteConfirm(false) }
  }

  async function fetchChapters() {
    const token = await getToken()
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    const res = await axios.get(`/api/stories/${story.id}/chapters`, { headers })
    return res.data ?? []
  }

  function storyFilename() {
    return story.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  }

  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function buildStoryHTML(chs) {
    let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHTML(story.title)}</title>`
    html += `<style>
      body { font-family: Georgia, 'Times New Roman', serif; max-width: 700px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; line-height: 1.7; }
      h1 { font-size: 2em; margin-bottom: 4px; }
      .byline { color: #666; font-size: 1.1em; margin-bottom: 24px; }
      .meta { color: #888; font-size: 0.9em; margin-bottom: 32px; border-bottom: 1px solid #ddd; padding-bottom: 16px; }
      .description { font-style: italic; color: #555; margin-bottom: 32px; }
      h2 { font-size: 1.4em; margin-top: 48px; border-bottom: 1px solid #eee; padding-bottom: 8px; }
      .chapter-content { margin-bottom: 48px; }
      @media print { body { margin: 0; } h2 { page-break-before: always; } }
    </style></head><body>`
    html += `<h1>${escapeHTML(story.title)}</h1>`
    html += `<p class="byline">by ${escapeHTML(story.author)}</p>`
    html += `<div class="meta">`
    html += `Fandom: ${escapeHTML(story.fandom)}`
    if (story.genre?.length) html += ` &middot; Genre: ${story.genre.map(g => escapeHTML(g)).join(', ')}`
    if (story.tags?.length) html += `<br>Tags: ${story.tags.map(t => escapeHTML(t)).join(', ')}`
    html += `<br>Rating: ${escapeHTML(story.rating)} &middot; Status: ${escapeHTML(story.status)} &middot; Words: ${(story.words ?? 0).toLocaleString()}`
    html += `</div>`
    if (story.description) html += `<p class="description">${escapeHTML(story.description)}</p>`
    for (const ch of chs) {
      html += `<h2>Chapter ${ch.number}: ${escapeHTML(ch.title)}</h2>`
      html += `<div class="chapter-content">${escapeHTML(ch.content ?? '')}</div>`
    }
    html += `</body></html>`
    return html
  }

  async function handleExport(format) {
    setExporting(true)
    setExportOpen(false)
    try {
      const chs = await fetchChapters()
      const fname = storyFilename()

      if (format === 'txt') {
        let text = `${'='.repeat(60)}\n${story.title}\nby ${story.author}\n${'='.repeat(60)}\n\n`
        text += `Fandom: ${story.fandom}\n`
        if (story.genre?.length) text += `Genre: ${story.genre.join(', ')}\n`
        if (story.tags?.length) text += `Tags: ${story.tags.join(', ')}\n`
        text += `Rating: ${story.rating} | Status: ${story.status} | Words: ${(story.words ?? 0).toLocaleString()}\n`
        if (story.description) text += `\nDescription:\n${story.description}\n`
        text += '\n'
        for (const ch of chs) {
          text += `${'-'.repeat(40)}\nChapter ${ch.number}: ${ch.title}\n${'-'.repeat(40)}\n\n`
          text += (ch.content ?? '').replace(/<[^>]+>/g, '') + '\n\n'
        }
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = `${fname}.txt`; a.click()
        URL.revokeObjectURL(url)

      } else if (format === 'html') {
        const html = buildStoryHTML(chs)
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = `${fname}.html`; a.click()
        URL.revokeObjectURL(url)

      } else if (format === 'pdf') {
        const { jsPDF } = await import('jspdf')
        const doc = new jsPDF({ unit: 'mm', format: 'a4' })
        const pageW = doc.internal.pageSize.getWidth()
        const pageH = doc.internal.pageSize.getHeight()
        const margin = 15
        const maxW = pageW - margin * 2
        let y = margin

        function checkPage(needed = 8) {
          if (y + needed > pageH - margin) { doc.addPage(); y = margin }
        }

        function writeLines(text, size, style = 'normal') {
          doc.setFontSize(size)
          doc.setFont('helvetica', style)
          const lines = doc.splitTextToSize(text, maxW)
          const lineH = size * 0.45
          for (const line of lines) {
            checkPage(lineH)
            doc.text(line, margin, y)
            y += lineH
          }
        }

        // Title
        doc.setFontSize(22)
        doc.setFont('helvetica', 'bold')
        doc.text(story.title, margin, y)
        y += 10

        doc.setFontSize(12)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(100)
        doc.text(`by ${story.author}`, margin, y)
        y += 8

        // Meta
        doc.setFontSize(9)
        doc.setTextColor(130)
        let meta = `Fandom: ${story.fandom}`
        if (story.genre?.length) meta += `  |  Genre: ${story.genre.join(', ')}`
        meta += `  |  Rating: ${story.rating}  |  Status: ${story.status}  |  Words: ${(story.words ?? 0).toLocaleString()}`
        writeLines(meta, 9)
        if (story.tags?.length) { y += 2; writeLines(`Tags: ${story.tags.join(', ')}`, 9) }
        y += 4
        doc.setDrawColor(200)
        doc.line(margin, y, pageW - margin, y)
        y += 8

        // Description
        if (story.description) {
          doc.setTextColor(80)
          writeLines(story.description, 10, 'italic')
          y += 6
        }

        // Chapters
        doc.setTextColor(0)
        for (const ch of chs) {
          checkPage(20)
          y += 6
          doc.setFontSize(14)
          doc.setFont('helvetica', 'bold')
          doc.text(`Chapter ${ch.number}: ${ch.title}`, margin, y)
          y += 4
          doc.setDrawColor(220)
          doc.line(margin, y, pageW - margin, y)
          y += 6

          const plainText = (ch.content ?? '')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n\n')
            .replace(/<[^>]+>/g, '')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ')
            .replace(/\n{3,}/g, '\n\n')
            .trim()

          doc.setFont('helvetica', 'normal')
          writeLines(plainText, 10.5)
          y += 8
        }

        doc.save(`${fname}.pdf`)
      }
    } catch (err) { console.error('[StoryReader] export story failed:', err) }
    setExporting(false)
  }

  // Close export dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false)
    }
    if (exportOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [exportOpen])

  // Close share dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false)
    }
    if (shareOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [shareOpen])

  // After story loads, check sessionStorage to see if user already confirmed mature content
  const isAdmin = authProfile?.isAdmin === true

  useEffect(() => {
    if (!story) return
    if (story.rating !== 'Mature' || isAdmin) { setMatureConfirmed(true); return }
    // If user is not signed in, redirect them to signup so we can collect DOB
    if (!user) {
      try {
        const from = window.location.pathname + window.location.search
        navigate('/signup', { state: { from }, replace: true })
        return
      } catch (err) {
        // ignore navigation errors in SSR/tests
      }
    }
    const confirmed = JSON.parse(sessionStorage.getItem('verse_mature_confirmed') ?? '[]')
    if (confirmed.includes(String(story.id))) setMatureConfirmed(true)
  }, [story, isAdmin])

  function handleMatureConfirm() {
    const confirmed = JSON.parse(sessionStorage.getItem('verse_mature_confirmed') ?? '[]')
    sessionStorage.setItem('verse_mature_confirmed', JSON.stringify([...confirmed, String(story.id)]))
    setMatureConfirmed(true)
    // Scroll to hash anchor after gate dismissal
    setTimeout(() => {
      const hash = window.location.hash
      if (hash) {
        const el = document.getElementById(hash.slice(1))
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('highlight-flash') }
      }
    }, 800)
  }

  // Save reading position and scroll to top on chapter change (skip if hash anchor present)
  useEffect(() => {
    try { localStorage.setItem(`verse_reading_${id}`, String(chapterNum)) } catch (err) { console.error('[StoryReader] save reading position to localStorage failed:', err) }
    if (!window.location.hash) window.scrollTo({ top: 0, behavior: 'smooth' })
    // Persist to server for "Continue Reading"
    if (user) {
      getToken().then(token => {
        if (!token) return
        axios.put(`/api/stories/reading-progress/${id}`, { chapter_number: chapterNum }, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(err => console.error('[StoryReader] Failed to update reading progress:', err))
      })
    }
  }, [chapterNum, id])

  useEffect(() => {
    try { localStorage.setItem('verse_reader_theme', readerTheme) } catch {}
  }, [readerTheme])

  // Reading progress bar
  const handleScroll = useCallback(() => {
    const el = contentRef.current
    if (!el) return
    const { top, height } = el.getBoundingClientRect()
    const viewH = window.innerHeight
    const scrolled = Math.max(0, viewH - top)
    setProgress(Math.min(100, (scrolled / (height + viewH)) * 100))
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  function goToChapter(n) {
    if (!user && story?.rating === 'Teen' && n > 1) {
      navigate('/login?reason=login_required', { state: { from: `/story/${id}?chapter=${n}` } })
      return
    }
    setSearchParams({ chapter: String(n) })
  }

  // Swipe between chapters on touch devices
  useEffect(() => {
    if (!('ontouchstart' in window)) return
    const el = articleRef.current
    if (!el) return
    function onStart(e) {
      touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY }
    }
    function onEnd(e) {
      const dx = e.changedTouches[0].clientX - touchRef.current.startX
      const dy = e.changedTouches[0].clientY - touchRef.current.startY
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return
      const idx = chapters.findIndex((c) => c.number === chapterNum)
      if (idx < 0) return
      if (dx < 0 && idx < chapters.length - 1) goToChapter(chapters[idx + 1].number)
      else if (dx > 0 && idx > 0) goToChapter(chapters[idx - 1].number)
    }
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchend', onEnd, { passive: true })
    return () => { el.removeEventListener('touchstart', onStart); el.removeEventListener('touchend', onEnd) }
  }, [chapterNum, chapters])

  // Canonicalize the URL when the requested chapter number has no matching row
  // (deleted chapter, stale bookmark, or out-of-range): redirect to the first
  // available chapter so the URL, sidebar highlight, and saved reading position
  // all agree — instead of silently rendering Chapter 1's body under a
  // ?chapter=N that points at nothing.
  useEffect(() => {
    if (chapters.length === 0) return
    if (!chapters.some((c) => c.number === chapterNum)) {
      setSearchParams({ chapter: String(chapters[0].number) }, { replace: true })
    }
  }, [chapters, chapterNum, setSearchParams])

  if (loadError) return (
    <div className="reader-page">
      <Navbar />
      <div style={{ textAlign: 'center', padding: '80px 24px', maxWidth: 420, margin: '0 auto' }}>
        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📖</div>
        <h2 style={{ marginBottom: '8px', color: 'var(--text)' }}>Couldn't load this story</h2>
        <p style={{ color: 'var(--text-faint)', marginBottom: '24px' }}>This might be a temporary issue. Try again or go back to browsing.</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button>
          <Link to="/browse" className="btn btn-outline">Browse stories</Link>
        </div>
      </div>
    </div>
  )

  if (!story) return (
    <div className="reader-page">
      <Navbar />
      <SkeletonLoader type="reader" />
    </div>
  )

  if (story.rating === 'Mature' && !matureConfirmed) return <MatureContentGate story={story} onConfirm={handleMatureConfirm} />

  const coverIdx      = story.coverIdx ?? 0
  const headerGrad    = COVER_GRADIENTS[coverIdx % COVER_GRADIENTS.length]
  const avatarColor   = story.authorAvatarGrad ?? AVATAR_COLORS[coverIdx % AVATAR_COLORS.length]
  const authorAvatarUrl = story.authorAvatarUrl ?? null
  // Resolve the displayed chapter by its stored `number`, not by array index.
  // `number` is unique per story but can be GAPPED (a deleted chapter, or a
  // draft-time reorder), so index-by-number would overrun the array and fall
  // back to Chapter 1. find-by-number serves the right row regardless of gaps.
  const matchIdx      = chapters.findIndex((c) => c.number === chapterNum)
  const resolvedIdx   = matchIdx >= 0 ? matchIdx : 0
  const chapter       = chapters[resolvedIdx]
  const totalChapters = chapters.length
  const prevNumber    = resolvedIdx > 0 ? chapters[resolvedIdx - 1].number : null
  const nextNumber    = resolvedIdx < chapters.length - 1 ? chapters[resolvedIdx + 1].number : null

  const statusStyle = STATUS_STYLE[story.status] ?? STATUS_STYLE.Ongoing
  const ratingStyle = RATING_STYLE[story.rating] ?? RATING_STYLE.General

  return (
    <div className="reader-page">
      <SEO
        title={decodeEntities(story.title)}
        description={decodeEntities(story.description?.slice(0, 160) || `Read "${story.title}" by ${story.author} on Verse.`)}
        image={story.coverUrl}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CreativeWork',
          name: story.title,
          author: { '@type': 'Person', name: story.author },
          description: story.description?.slice(0, 300),
          genre: story.genre,
          url: `https://versesite.net/story/${story.id}`,
        }}
      />
      {/* Reading progress bar */}
      <div className="read-progress" style={{ width: `${progress}%` }} />

      <Navbar />

      {/* ── Story header ── */}
      <header className="story-header" style={{ background: headerGrad }}>
        <div className="story-header__blobs">
          <div className="story-header__blob story-header__blob--1" />
          <div className="story-header__blob story-header__blob--2" />
        </div>

        <div className="story-header__inner">
          {/* Breadcrumb */}
          <nav className="story-header__breadcrumb">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/browse">Browse</Link>
            <span>›</span>
            <span>{decodeEntities(story.fandom)}</span>
          </nav>

          <div className="story-header__content">
            {/* Cover art */}
            <div className="story-header__cover">
              {story.coverUrl
                ? <img src={story.coverUrl} alt={story.title} className="story-header__cover-img" />
                : (
                  <div className="story-header__cover-art" style={{ background: headerGrad }}>
                    <span className="story-header__cover-initial">
                      {story.title[0]}
                    </span>
                  </div>
                )
              }
            </div>

            <div className="story-header__info">
              <div className="story-header__badges">
                <span className="story-header__badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                  {story.status}
                </span>
                {!(story.sexuallyExplicit && story.rating === 'Mature') && (
                  <span className="story-header__badge" style={{ background: ratingStyle.bg, color: ratingStyle.color }}>
                    {story.rating}
                  </span>
                )}
                {story.sexuallyExplicit && (
                  <span className="story-header__badge" style={{ background: 'rgba(225, 29, 72, 0.9)', color: '#fff' }}>
                    Sexually Explicit
                  </span>
                )}
                {story.genre?.map((g) => (
                  <span key={g} className="story-header__genre-badge">{decodeEntities(g)}</span>
                ))}
              </div>

              <h1 className="story-header__title">{decodeEntities(story.title)}</h1>

              <div className="story-header__author-row">
                <div className="story-header__avatar" style={{ background: authorAvatarUrl ? undefined : avatarColor }}>
                  {authorAvatarUrl
                    ? <img src={authorAvatarUrl} alt={story.author} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : story.author[0].toUpperCase()}
                </div>
                <span className="story-header__by">by</span>
                <Link to={`/user/${story.authorUsername ?? story.author}`} className="story-header__author">{decodeEntities(story.author)}</Link>
                <span className="story-header__fandom">· {decodeEntities(story.fandom)}</span>
              </div>

              <p className="story-header__desc">{decodeEntities(story.description)}</p>

              <div className="story-header__tags">
                {story.tags?.map((t) => (
                  <span key={t} className="story-header__tag">#{decodeEntities(t)}</span>
                ))}
              </div>

              <div className="story-header__stats">
                <Stat icon="❤️" value={formatNum(likeCount)}   label="Likes" />
                <Stat icon="👁️" value={formatNum(story.views)}   label="Views" />
                <Stat icon="📖" value={story.chapters}           label="Chapters" />
                <Stat icon="✍️" value={formatNum(story.words)}   label="Words" />
              </div>

              <div className="story-header__actions">
                <button
                  className={`btn story-header__action-btn${liked ? ' story-header__action-btn--liked' : ''}`}
                  onClick={toggleLike}
                  disabled={!user}
                >
                  {liked ? '❤️' : '🤍'} {liked ? 'Liked' : 'Like'}
                </button>
                <button
                  className={`btn story-header__action-btn${bookmarked ? ' story-header__action-btn--active story-header__action-btn--bookmark' : ''}`}
                  onClick={handleBookmark}
                  disabled={!user || bookmarkBusy}
                >
                  {bookmarked ? '🔖' : '📑'} {bookmarkBusy ? '…' : bookmarked ? 'Saved' : 'Save'}
                </button>
                <div ref={shareRef} style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    className="btn story-header__action-btn"
                    onClick={() => setShareOpen(o => !o)}
                  >
                    {copied ? '✓ Copied!' : '↗ Share'}
                  </button>
                  {shareOpen && (() => {
                    const url = window.location.href
                    const text = `${story.title} by ${story.author} on Verse`
                    return (
                      <div className="export-dropdown" style={{ minWidth: 160 }}>
                        <button
                          className="export-dropdown__item"
                          aria-label="Copy link to clipboard"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(url)
                              setCopied(true)
                            } catch (err) {
                              try {
                                const ta = document.createElement('textarea')
                                ta.value = url
                                document.body.appendChild(ta)
                                ta.select()
                                document.execCommand && document.execCommand('copy')
                                document.body.removeChild(ta)
                                setCopied(true)
                              } catch (_) {
                                // ignore
                              }
                            } finally {
                              setShareOpen(false)
                              setTimeout(() => setCopied(false), 2000)
                            }
                          }}
                        >📋 Copy link</button>
                        <button className="export-dropdown__item" onClick={() => {
                          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank')
                          setShareOpen(false)
                        }}>𝕏 Twitter / X</button>
                        <button className="export-dropdown__item" onClick={() => {
                          window.open(`https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(story.title + ' by ' + story.author)}`, '_blank')
                          setShareOpen(false)
                        }}>🔗 Reddit</button>
                        <button className="export-dropdown__item" onClick={() => {
                          window.open(`https://www.tumblr.com/widgets/share/tool?canonicalUrl=${encodeURIComponent(url)}&title=${encodeURIComponent(story.title)}`, '_blank')
                          setShareOpen(false)
                        }}>📝 Tumblr</button>
                        <button className="export-dropdown__item" onClick={() => {
                          window.location.href = `mailto:?subject=${encodeURIComponent('Check out "' + story.title + '" on Verse')}&body=${encodeURIComponent(story.title + ' by ' + story.author + '\n\n' + url)}`
                          setShareOpen(false)
                        }}>✉️ Email</button>
                        {typeof navigator.share === 'function' && (
                          <button className="export-dropdown__item" onClick={() => {
                            navigator.share({ title: story.title, text, url }).catch(() => {})
                            setShareOpen(false)
                          }}>📱 Share via…</button>
                        )}
                      </div>
                    )
                  })()}
                </div>
                {/* Tip button temporarily hidden
                {user && story.userId !== user.id && !story.sexuallyExplicit && story.tipsEnabled && (
                  <button
                    className="btn story-header__action-btn"
                    onClick={() => setTipModalOpen(true)}
                  >
                    🪙 Tip
                  </button>
                )}
                */}
                {user && story.userId === user.id && (
                  <>
                    <Link
                      to={`/story/${story.id}/edit`}
                      className="btn story-header__action-btn"
                    >
                      ✏️ Edit story
                    </Link>
                    {story.published && (
                      <button
                        className="btn story-header__action-btn"
                        onClick={async () => {
                          if (!window.confirm('Unpublish this story? It will be removed from browse and only visible to you as a draft.')) return
                          try {
                            const token = await getToken()
                            await axios.put(`/api/stories/${story.id}`, { published: false }, { headers: { Authorization: `Bearer ${token}` } })
                            navigate(`/story/${story.id}/edit`)
                          } catch (err) {
                            console.error('[unpublish]', err)
                          }
                        }}
                      >
                        📤 Unpublish
                      </button>
                    )}
                    {story.published && (
                      <div ref={exportRef} style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="btn story-header__action-btn"
                          onClick={() => setExportOpen(p => !p)}
                          disabled={exporting}
                        >
                          {exporting ? 'Exporting…' : '📥 Export story ▾'}
                        </button>
                        {exportOpen && (
                          <div className="export-dropdown">
                            <button className="export-dropdown__item" onClick={() => handleExport('pdf')}>PDF</button>
                            <button className="export-dropdown__item" onClick={() => handleExport('html')}>HTML</button>
                            <button className="export-dropdown__item" onClick={() => handleExport('txt')}>Plain Text</button>
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      className="btn story-header__action-btn story-header__action-btn--report"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting…' : deleteConfirm ? 'Are you sure?' : '🗑️ Delete story'}
                    </button>
                    {deleteConfirm && !deleting && (
                      <button
                        className="btn story-header__action-btn"
                        onClick={() => setDeleteConfirm(false)}
                      >
                        Cancel
                      </button>
                    )}
                  </>
                )}
                {user && story.userId !== user.id && (
                <button
                  className="btn story-header__action-btn story-header__action-btn--report"
                  onClick={() => chapter ? setChapterReportOpen(true) : setReportOpen(true)}
                  title={chapter ? 'Report this chapter' : 'Report this story'}
                >
                  {chapter ? '⚑ Report Chapter' : '⚑ Report'}
                </button>
                )}
                {isAdmin && (
                  <button
                    className="btn story-header__action-btn"
                    style={{ color: story.hidden ? '#16A34A' : '#DC2626', borderColor: story.hidden ? '#16A34A' : '#DC2626' }}
                    disabled={hideBusy}
                    onClick={async () => {
                      setHideBusy(true)
                      try {
                        const token = await getToken()
                        const headers = token ? { Authorization: `Bearer ${token}` } : {}
                        await axios.patch(`/api/admin/hide/story/${story.id}`, { hidden: !story.hidden }, { headers })
                        setStory(prev => ({ ...prev, hidden: !prev.hidden }))
                      } catch (err) { console.error('[StoryReader] toggle story visibility failed:', err) }
                      setHideBusy(false)
                    }}
                  >
                    {hideBusy ? '…' : story.hidden ? 'Unhide' : 'Hide'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Reader body ── */}
      <div className="reader-layout">
        {/* Main column */}
        <main className="reader-main" ref={contentRef}>
          {chapters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--text-faint)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>✍️</div>
              <p>No chapters published yet. Check back soon!</p>
            </div>
          ) : (
          <>
          <article className="reader-article" ref={articleRef} data-reader-theme={readerTheme}>
            <div className="reader-article__header">
              <div className="reader-article__chapter-label">
                Chapter {chapter.number} of {totalChapters}
              </div>
              <h2 className="reader-article__title">{decodeEntities(chapter.title)}</h2>
              <div className="reader-article__meta">
                <span>{chapter.words?.toLocaleString()} words</span>
                {chapter.publishedAt && (
                  <>
                    <span className="reader-dot" />
                    <span>Published {new Date(chapter.publishedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </>
                )}
              </div>

              {/* Font size controls */}
              <div className="reader-controls">
                <span className="reader-controls__label">Text size</span>
                {['sm', 'md', 'lg'].map((sz) => (
                  <button
                    key={sz}
                    className={`reader-controls__btn${fontSize === sz ? ' reader-controls__btn--active' : ''}`}
                    onClick={() => setFontSize(sz)}
                    style={{ fontSize: sz === 'sm' ? '0.8rem' : sz === 'md' ? '0.95rem' : '1.1rem' }}
                  >
                    A
                  </button>
                ))}
              </div>

              {/* Reader theme */}
              <div className="reader-controls" style={{ marginTop: 10 }}>
                <span className="reader-controls__label">Theme</span>
                {[
                  { key: 'light',    color: '#FFFFFF',  border: '#E5E7EB' },
                  { key: 'dark',     color: '#1A1A2E',  border: '#1A1A2E' },
                  { key: 'sepia',    color: '#F4ECD8',  border: '#D4C5A9' },
                  { key: 'sage',     color: '#E8EDE5',  border: '#C5CCBF' },
                  { key: 'rose',     color: '#FDE8E8',  border: '#E8B4B8' },
                  { key: 'midnight', color: '#191933',  border: '#191933' },
                  { key: 'slate',    color: '#E2E8F0',  border: '#CBD5E1' },
                ].map((t) => (
                  <button
                    key={t.key}
                    className={`reader-theme-swatch${readerTheme === t.key ? ' reader-theme-swatch--active' : ''}`}
                    onClick={() => setReaderTheme(t.key)}
                    aria-label={`${t.key} theme`}
                    title={t.key.charAt(0).toUpperCase() + t.key.slice(1)}
                    style={{ background: t.color, borderColor: readerTheme === t.key ? 'var(--accent)' : t.border }}
                  />
                ))}
              </div>
            </div>

            <div
              className={`reader-content reader-content--${fontSize}`}
              dangerouslySetInnerHTML={{ __html: sanitizeStoryContent(
                Array.isArray(chapter.content)
                  ? chapter.content.map(p => p.startsWith('<') ? p : `<p>${p}</p>`).join('')
                  : (chapter.content && !/<p[\s>]/i.test(chapter.content))
                    ? chapter.content.replace(/<\/?div[^>]*>/gi, '\n').replace(/<br\s*\/?>/gi, '\n').split(/\n{2,}/).filter(Boolean).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
                    : (chapter.content || '')
              ) }}
            />
            {totalChapters > 1 && (
              <p className="reader-swipe-hint">← Swipe to navigate chapters →</p>
            )}
          </article>

          <ChapterNav
            currentNumber={chapter?.number}
            count={totalChapters}
            prevNumber={prevNumber}
            nextNumber={nextNumber}
            validNumbers={chapters.map((c) => c.number)}
            onNavigate={goToChapter}
            bottom
          />

          {/* Copyright notice */}
          <div className="copyright-notice">
            <span className="copyright-notice__text">
              © {new Date().getFullYear()} {story.author} · All rights reserved. This work is posted on Verse
              and protected by copyright. Do not reproduce without the author's permission.
            </span>
            <button
              className="copyright-notice__report-link"
              onClick={() => chapter ? setChapterReportOpen(true) : setReportOpen(true)}
            >
              Report copyright violation
            </button>
          </div>

          {/* Comments */}
          {story.disableComments ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-faint)', fontSize: '0.9rem' }}>
              Comments are disabled on this story.
            </div>
          ) : (
          <CommentsSection
            storyId={story.id}
            comments={comments}
            onCommentAdded={(c) => setComments((prev) => [...prev, c])}
            onCommentDeleted={(id) => setComments((prev) => prev.filter((c) => c.id !== id))}
            onCommentUpdated={(updated) => setComments((prev) => prev.map((c) => c.id === updated.id ? { ...c, content: updated.content } : c))}
            comment={comment}
            onCommentChange={setComment}
            user={user}
          />
          )}
          </>
          )}
        </main>

        {/* Sidebar */}
        <aside className="reader-sidebar">
          <ChapterList
            chapters={chapters}
            total={totalChapters}
            current={chapter?.number}
            onSelect={goToChapter}
          />
          <AuthorCard author={story.authorUsername ?? story.author} avatarColor={avatarColor} avatarUrl={authorAvatarUrl} />
        </aside>
      </div>

      {reportOpen && (
        <ReportModal
          contentType="story"
          contentId={story.id}
          contentTitle={story.title}
          onClose={() => setReportOpen(false)}
        />
      )}
      {chapterReportOpen && chapter && (
        <ReportModal contentType="chapter" contentId={chapter.id} onClose={() => setChapterReportOpen(false)} />
      )}

      {tipModalOpen && (
        <TipModal
          storyId={story.id}
          storyTitle={story.title}
          authorName={story.author}
          onClose={() => setTipModalOpen(false)}
          onSuccess={() => {}}
        />
      )}
      {/* Copy toast (screen-reader friendly via aria-live) */}
      <div
        aria-live="polite"
        role="status"
        style={{
          position: 'fixed',
          right: 16,
          bottom: 24,
          background: 'rgba(0,0,0,0.8)',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: '0.9rem',
          opacity: copied ? 1 : 0,
          transform: copied ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 180ms, transform 180ms',
          pointerEvents: 'none',
        }}
      >
        {copied ? 'Link copied to clipboard' : null}
      </div>
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function Stat({ icon, value, label }) {
  return (
    <div className="story-header__stat">
      <span>{icon} {value}</span>
      <span className="story-header__stat-label">{label}</span>
    </div>
  )
}

function ChapterNav({ currentNumber, count, prevNumber, nextNumber, validNumbers, onNavigate, bottom }) {
  const [inputVal, setInputVal] = useState(String(currentNumber ?? ''))

  useEffect(() => {
    setInputVal(String(currentNumber ?? ''))
  }, [currentNumber])

  function handleGo(e) {
    e?.preventDefault?.()
    const num = parseInt(inputVal, 10)
    // Jump only to a chapter number that actually exists; otherwise reset.
    // No contiguous-range assumption — validity is membership, not 1..count.
    if (validNumbers.includes(num) && num !== currentNumber) onNavigate(num)
    else setInputVal(String(currentNumber ?? ''))
  }

  return (
    <div className={`chapter-nav${bottom ? ' chapter-nav--bottom' : ''}`}>
      <button
        className="btn chapter-nav__btn"
        disabled={prevNumber == null}
        onClick={() => onNavigate(prevNumber)}
      >
        ← Prev
      </button>

      <form className="chapter-nav__indicator" onSubmit={handleGo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <span>Ch.</span>
        <input
          type="number"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onBlur={handleGo}
          className="chapter-nav__input"
          style={{
            width: `${Math.max(2, String(count).length + 1)}ch`,
            textAlign: 'center', fontSize: 'inherit', fontWeight: 'inherit',
            background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)',
            padding: '2px 0', color: 'inherit', outline: 'none',
            MozAppearance: 'textfield',
          }}
        />
        <span>/ {count}</span>
      </form>

      <button
        className="btn chapter-nav__btn chapter-nav__btn--next"
        disabled={nextNumber == null}
        onClick={() => onNavigate(nextNumber)}
      >
        Next →
      </button>
    </div>
  )
}

function ChapterList({ chapters, total, current, onSelect }) {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? chapters : chapters.slice(0, 12)
  const hasMore = !showAll && total > 12

  return (
    <div className="sidebar-card">
      <div className="sidebar-card__title">Chapters</div>
      <div className="chapter-list">
        {visible.map((ch) => (
          <button
            key={ch.number}
            className={`chapter-list__item${ch.number === current ? ' chapter-list__item--active' : ''}`}
            onClick={() => onSelect(ch.number)}
          >
            <span className="chapter-list__num">{ch.number}</span>
            <span className="chapter-list__name">{decodeEntities(ch.title)}</span>
            {ch.words && (
              <span className="chapter-list__words">{ch.words?.toLocaleString()}w</span>
            )}
          </button>
        ))}
        {hasMore && (
          <button className="chapter-list__more" onClick={() => setShowAll(true)}>
            +{total - 12} more chapters
          </button>
        )}
      </div>
      <BackToTop />
    </div>
  )
}

function AuthorCard({ author, avatarColor, avatarUrl: storyAvatarUrl }) {
  // myProfile = currently logged-in viewer's profile (from AuthContext)
  // profile   = author's profile (local state, fetched below)
  const { user, profile: myProfile, getToken } = useAuth()
  const [profile, setProfile]   = useState(null)
  const [storyCount, setStoryCount] = useState(null)
  const [following, setFollowing]   = useState(false)
  const [requested, setRequested]   = useState(false)
  const [requestId, setRequestId]   = useState(null)
  const [followBusy, setFollowBusy] = useState(false)

  useEffect(() => {
    axios.get(`/api/profile/${author}`)
      .then(res => {
        setProfile(res.data.profile)
        setStoryCount(res.data.stories?.length ?? null)
      })
      .catch(() => {})
  }, [author])

  useEffect(() => {
    if (!user) return
    getToken().then(token => {
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      axios.get(`/api/follows/status/${author}`, { headers })
        .then(res => {
          setFollowing(res.data.following)
          setRequested(res.data.requested ?? false)
          setRequestId(res.data.requestId ?? null)
        })
        .catch(err => console.error('[StoryReader] Failed to fetch follow status:', err))
    })
  }, [user, author])

  async function handleFollow() {
    if (followBusy) return
    if (navigator.vibrate) navigator.vibrate(10)
    setFollowBusy(true)
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      if (following) {
        await axios.delete(`/api/follows/${author}`, { headers })
        setFollowing(false)
      } else if (requested) {
        // Cancel pending request via DELETE /api/follow-requests/:requestId
        if (requestId) {
          await axios.delete(`/api/follow-requests/${requestId}`, { headers })
        }
        setRequested(false)
        setRequestId(null)
      } else {
        // Server decides follows vs follow_requests based on target.privacy_mode.
        const res = await axios.post(`/api/follows/${author}`, {}, { headers })
        if (res.data.requested) {
          setRequested(true)
          setRequestId(res.data.requestId ?? null)
        } else {
          setFollowing(true)
        }
      }
    } catch (err) { console.error('[StoryReader] toggle follow failed:', err) } finally {
      setFollowBusy(false)
    }
  }

  const displayName  = profile?.displayName ?? author
  const grad         = profile?.avatarGrad ?? avatarColor
  const authorAvatar = profile?.avatarUrl ?? storyAvatarUrl ?? null

  function formatJoined(iso) {
    if (!iso) return null
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  function formatNum(n) {
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
    return String(n)
  }

  // PR 2: read from profile (server-authoritative) not user_metadata (client-writable).
  const isOwn = myProfile?.username === author

  return (
    <div className="sidebar-card author-card">
      <div className="sidebar-card__title">About the author</div>
      <div className="author-card__header">
        <Link to={`/user/${author}`}>
          <div className="author-card__avatar" style={{ background: authorAvatar ? undefined : grad }}>
            {authorAvatar
              ? <img src={authorAvatar} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : displayName[0].toUpperCase()}
          </div>
        </Link>
        <div>
          <Link to={`/user/${author}`} className="author-card__name" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
            {displayName}
            {profile?.role === 'admin' && <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--tag-rating-bg)', color: 'var(--tag-rating-text)', fontSize: '0.7rem', fontWeight: 600 }}>🛡️ Admin</span>}
            {profile?.role === 'moderator' && <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--tag-genre-bg)', color: 'var(--tag-genre-text)', fontSize: '0.7rem', fontWeight: 600 }}>Moderator</span>}
            {profile?.legacy && <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--tag-trope-bg)', color: 'var(--tag-trope-text)', fontSize: '0.7rem', fontWeight: 600 }}>Legacy</span>}
          </Link>
          {profile?.joinedAt && (
            <div className="author-card__joined">Joined {formatJoined(profile.joinedAt)}</div>
          )}
        </div>
      </div>

      {profile?.bio && (
        <div className="author-card__bio" dangerouslySetInnerHTML={{ __html: sanitizeBio(profile.bio) }} />
      )}
      {!profile?.bio && profile?.tagline && (
        <p className="author-card__bio">{profile.tagline}</p>
      )}

      {(storyCount !== null || profile?.followers != null) && (
        <div className="author-card__stats">
          {storyCount !== null && (
            <div className="author-card__stat"><strong>{storyCount}</strong> {storyCount === 1 ? 'Story' : 'Stories'}</div>
          )}
          {profile?.followers != null && (
            <div className="author-card__stat"><strong>{formatNum(profile.followers)}</strong> Followers</div>
          )}
        </div>
      )}

      {!isOwn && user && (() => {
        // Three-state follow button. Initial label uses requiresFollowRequest
        // from the public-profile response so protected accounts show
        // "Request to follow" up front rather than flipping after click.
        let label
        if (followBusy)     label = '…'
        else if (following) label = '✓ Following'
        else if (requested) label = '✓ Requested'
        else if (profile?.requiresFollowRequest) label = '+ Request to follow'
        else                label = '+ Follow'
        const isInactive = following || requested
        return (
          <button
            className={`btn author-card__follow${isInactive ? ' btn-outline' : ' btn-primary'}`}
            onClick={handleFollow}
            disabled={followBusy}
          >
            {label}
          </button>
        )
      })()}
      {!user && (
        <Link to="/login" className="btn btn-primary author-card__follow">Follow author</Link>
      )}
    </div>
  )
}

function CommentsSection({ storyId, comments, onCommentAdded, onCommentDeleted, onCommentUpdated, comment, onCommentChange, user }) {
  const { profile, getToken } = useAuth()
  const blockedIds = useBlocks()
  const [posting, setPosting]   = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [error, setError]       = useState('')
  const [likeBusy, setLikeBusy]       = useState(new Set())
  const [editingId, setEditingId]     = useState(null)
  const [editText, setEditText]       = useState('')
  const [saving, setSaving]           = useState(false)
  const [editError, setEditError]     = useState('')
  const [menuOpenId, setMenuOpenId]   = useState(null)
  const [replyingToId, setReplyingToId] = useState(null) // comment id being replied to
  const [replyText, setReplyText]       = useState('')
  const [replyPosting, setReplyPosting] = useState(false)
  const [replyError, setReplyError]     = useState('')
  const [reportingCommentId, setReportingCommentId] = useState(null)
  const [likedSet, setLikedSet] = useState(new Set())
  const [likeCounts, setLikeCounts] = useState({})

  // Initialize like counts from comments and fetch liked status for logged-in user
  useEffect(() => {
    if (!comments || comments.length === 0) return
    // Initialize counts from the likes field on each comment
    const counts = {}
    comments.forEach(c => { counts[c.id] = c.likes ?? 0 })
    setLikeCounts(counts)

    if (!user) return
    // Batch check which comments the current user has liked
    async function fetchLikedStatus() {
      try {
        const token = await getToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const { data } = await axios.post('/api/comments/liked-batch', { commentIds: comments.map(c => c.id) }, { headers })
        setLikedSet(new Set(data.likedIds ?? []))
      } catch (err) { console.error('[StoryReader] fetch comment liked status failed:', err) }
    }
    fetchLikedStatus()
  }, [comments, user])

  async function handleLike(commentId) {
    if (!user) return
    if (likeBusy.has(commentId)) return
    if (navigator.vibrate) navigator.vibrate(10)
    setLikeBusy(prev => new Set(prev).add(commentId))
    const wasLiked = likedSet.has(commentId)
    // Optimistic update
    setLikedSet(prev => {
      const next = new Set(prev)
      if (wasLiked) next.delete(commentId)
      else next.add(commentId)
      return next
    })
    setLikeCounts(prev => ({
      ...prev,
      [commentId]: (prev[commentId] ?? 0) + (wasLiked ? -1 : 1)
    }))
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      if (wasLiked) {
        await axios.delete(`/api/comments/${commentId}/like`, { headers })
      } else {
        await axios.post(`/api/comments/${commentId}/like`, {}, { headers })
      }
    } catch {
      // Revert on error
      setLikedSet(prev => {
        const next = new Set(prev)
        if (wasLiked) next.add(commentId)
        else next.delete(commentId)
        return next
      })
      setLikeCounts(prev => ({
        ...prev,
        [commentId]: (prev[commentId] ?? 0) + (wasLiked ? 1 : -1)
      }))
    } finally {
      setLikeBusy(prev => { const next = new Set(prev); next.delete(commentId); return next })
    }
  }

  function startEdit(c) {
    setEditingId(c.id)
    setEditText(c.content)
    setEditError('')
  }

  function cancelEdit() {
    setEditingId(null)
    setEditText('')
    setEditError('')
  }

  async function handleSaveEdit(id) {
    if (!editText.trim() || saving) return
    setSaving(true)
    setEditError('')
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const { data } = await axios.put(`/api/comments/${id}`, { content: editText }, { headers })
      onCommentUpdated(data)
      setEditingId(null)
    } catch (err) {
      setEditError(err.response?.data?.error || err.message || 'Failed to save edit.')
    } finally {
      setSaving(false)
    }
  }

  // Close ••• menu on outside click
  useEffect(() => {
    if (!menuOpenId) return
    function handler() { setMenuOpenId(null) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpenId])

  async function handleDelete(id) {
    setDeleting(id)
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      await axios.delete(`/api/comments/${id}`, { headers })
      onCommentDeleted(id)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete comment.')
    } finally {
      setDeleting(null)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!comment.trim() || posting) return
    setPosting(true)
    setError('')
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const { data } = await axios.post(`/api/comments/${storyId}`, { content: comment }, { headers })
      onCommentAdded(data)
      onCommentChange('')
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to post comment. Please try again.')
    } finally {
      setPosting(false)
    }
  }

  async function handleReply(commentId) {
    if (!replyText.trim() || replyPosting) return
    setReplyPosting(true)
    setReplyError('')
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      // Flatten at 2 levels: if replying to a reply, group under the same parent
      const target = comments.find(c => c.id === commentId)
      const parentId = target?.parent_id ? target.parent_id : commentId
      // If replying to a reply (not the top-level parent), store the actual target for @mention
      const replyToId = target?.parent_id ? commentId : undefined
      const { data } = await axios.post(`/api/comments/${storyId}`, { content: replyText, parent_id: parentId, reply_to_id: replyToId }, { headers })
      // Enrich with reply_to_username so it displays immediately
      if (replyToId && target?.profiles?.username) {
        data.reply_to_username = target.profiles.username
      }
      onCommentAdded(data)
      setReplyText('')
      setReplyingToId(null)
    } catch (err) {
      setReplyError(err.response?.data?.error || 'Failed to post reply.')
    } finally {
      setReplyPosting(false)
    }
  }

  function formatTime(iso) {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const avatarInitial = user?.email?.[0]?.toUpperCase() ?? '?'
  const myAvatarUrl   = profile?.avatarUrl ?? null

  return (
    <section className="comments-section">
      <h3 className="comments-section__title">
        Comments <span className="comments-section__count">{comments.length}</span>
      </h3>

      {/* Comment form */}
      {user ? (
        <form className="comment-form" onSubmit={handleSubmit}>
          <div className="comment-form__avatar" style={{ background: myAvatarUrl ? undefined : (profile?.avatarGrad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)') }}>
            {myAvatarUrl
              ? <img src={myAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : avatarInitial}
          </div>
          <div className="comment-form__body">
            {error && <div className="comment-form__error">{error}</div>}
            <textarea
              className="comment-form__input"
              placeholder="Leave a comment for the author…"
              value={comment}
              onChange={(e) => { onCommentChange(e.target.value); setError('') }}
              rows={3}
            />
            <div className="comment-form__footer">
              <button
                type="submit"
                className="btn btn-primary"
                style={{ padding: '8px 20px', fontSize: '0.85rem' }}
                disabled={!comment.trim() || posting}
              >
                {posting ? 'Posting…' : 'Post comment'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <p className="comments-section__login">Sign in to leave a comment.</p>
      )}

      {/* Comment list */}
      <div className="comment-list">
        {comments.filter(c => !c.parent_id && !blockedIds.has(c.user_id)).length === 0 && (
          <p className="comment-list__empty">No comments yet. Be the first!</p>
        )}
        {comments.filter(c => !c.parent_id && !blockedIds.has(c.user_id)).map((c) => {
          const replies = comments.filter(r => r.parent_id === c.id && !blockedIds.has(r.user_id))
          return <CommentItem
            key={c.id}
            c={c}
            replies={replies}
            allComments={comments}
            blockedIds={blockedIds}
            user={user}
            editingId={editingId}
            editText={editText}
            setEditText={setEditText}
            editError={editError}
            saving={saving}
            deleting={deleting}
            menuOpenId={menuOpenId}
            setMenuOpenId={setMenuOpenId}
            replyingToId={replyingToId}
            setReplyingToId={(id) => { setReplyingToId(id); setReplyText(''); setReplyError('') }}
            replyText={replyText}
            setReplyText={setReplyText}
            replyPosting={replyPosting}
            replyError={replyError}
            onStartEdit={startEdit}
            onCancelEdit={cancelEdit}
            onSaveEdit={handleSaveEdit}
            onDelete={handleDelete}
            onReply={handleReply}
            formatTime={formatTime}
            myAvatarUrl={myAvatarUrl}
            myAvatarInitial={avatarInitial}
            myAvatarGrad={profile?.avatarGrad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)'}
            onReport={setReportingCommentId}
            likedSet={likedSet}
            likeCounts={likeCounts}
            onLike={handleLike}
          />
        })}
      </div>

      {reportingCommentId && (
        <ReportModal
          contentType="comment"
          contentId={reportingCommentId}
          onClose={() => setReportingCommentId(null)}
        />
      )}
    </section>
  )
}

function CommentItem({
  c, replies, allComments = [], blockedIds = new Set(), user, editingId, editText, setEditText, editError, saving,
  deleting, menuOpenId, setMenuOpenId, replyingToId, setReplyingToId,
  replyText, setReplyText, replyPosting, replyError,
  onStartEdit, onCancelEdit, onSaveEdit, onDelete, onReply, formatTime,
  myAvatarUrl, myAvatarInitial, myAvatarGrad, isReply = false, onReport,
  likedSet = new Set(), likeCounts = {}, onLike = () => {},
}) {
  const [showAllReplies, setShowAllReplies] = useState(false)
  const prof     = c.profiles ?? {}
  const name     = prof.display_name || prof.username || 'Anonymous'
  const initial  = name[0]?.toUpperCase() ?? '?'
  const grad     = prof.avatar_grad || 'linear-gradient(135deg, #7C3AED, #EC4899)'
  const imgUrl   = prof.avatar_url ?? null

  return (
    <div id={`comment-${c.id}`} className={isReply ? 'comment comment--reply' : 'comment'}>
      <Link to={`/user/${prof.username}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
        <div className="comment__avatar" style={{ background: imgUrl ? undefined : grad }}>
          {imgUrl
            ? <img src={imgUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : initial}
        </div>
      </Link>
      <div className="comment__body">
        <div className="comment__header">
          <Link to={`/user/${prof.username}`} className="comment__author" style={{ textDecoration: 'none', color: 'inherit' }}>{name}</Link>
          {isReply && c.reply_to_username && (
            <Link to={`/user/${c.reply_to_username}`} style={{ fontSize: '0.78rem', color: 'var(--text-faint)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              ↩ <span style={{ color: 'var(--purple)' }}>@{c.reply_to_username}</span>
            </Link>
          )}
          <span className="comment__time">{formatTime(c.created_at)}</span>
          {user && user.id !== c.user_id && onReport && editingId !== c.id && (
            <button className="comment__report-btn" onClick={() => onReport(c.id)} aria-label="Report comment" title="Report comment">
              ⚑
            </button>
          )}
          {user?.id === c.user_id && editingId !== c.id && (
            <div className="comment__menu-wrap">
              <button className="comment__menu-btn" onClick={() => setMenuOpenId(id => id === c.id ? null : c.id)} aria-label="Comment options">
                •••
              </button>
              {menuOpenId === c.id && (
                <div className="comment__menu" onMouseDown={e => e.stopPropagation()}>
                  <button onClick={() => { setMenuOpenId(null); onStartEdit(c) }}>Edit</button>
                  <button className="comment__menu-delete" onClick={() => { setMenuOpenId(null); onDelete(c.id) }} disabled={deleting === c.id}>
                    {deleting === c.id ? '…' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {editingId === c.id ? (
          <div className="comment__edit-form">
            {editError && <div className="comment-form__error">{editError}</div>}
            <textarea className="comment-form__input" value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} autoFocus />
            <div className="comment__edit-actions">
              <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={() => onSaveEdit(c.id)} disabled={!editText.trim() || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={onCancelEdit} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <p className="comment__text">{decodeEntities(c.content)}</p>
        )}

        {/* Reply & Like buttons */}
        {editingId !== c.id && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
            {user && (
              <button className="comment__reply-btn" onClick={() => setReplyingToId(replyingToId === c.id ? null : c.id)}>
                {replyingToId === c.id ? 'Cancel' : 'Reply'}
              </button>
            )}
            <button
              className={`comment__like${likedSet.has(c.id) ? ' comment__like--active' : ''}`}
              onClick={() => onLike(c.id)}
              disabled={!user}
            >
              {likedSet.has(c.id) ? '\u2665' : '\u2661'}{(likeCounts[c.id] ?? 0) > 0 ? ` ${likeCounts[c.id]}` : ''}
            </button>
          </div>
        )}

        {/* Inline reply form */}
        {replyingToId === c.id && (
          <div className="comment__reply-form">
            <div className="comment__reply-avatar" style={{ background: myAvatarUrl ? undefined : myAvatarGrad }}>
              {myAvatarUrl
                ? <img src={myAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : myAvatarInitial}
            </div>
            <div className="comment__reply-body">
              {replyError && <div className="comment-form__error">{replyError}</div>}
              <textarea
                className="comment-form__input"
                placeholder={`Reply to ${name}…`}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                rows={2}
                autoFocus
              />
              <div className="comment__edit-actions">
                <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={() => onReply(c.id)} disabled={!replyText.trim() || replyPosting}>
                  {replyPosting ? 'Posting…' : 'Post reply'}
                </button>
                <button className="btn" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={() => setReplyingToId(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Replies */}
        {replies.length > 0 && (
          <div className="comment__replies">
            {!showAllReplies && replies.length > 2 && (
              <button className="comment__show-more" onClick={() => setShowAllReplies(true)}>
                Show {replies.length - 2} more {replies.length - 2 === 1 ? 'reply' : 'replies'}
              </button>
            )}
            {(showAllReplies ? replies : replies.slice(-2)).map(r => (
              <CommentItem
                key={r.id}
                c={r}
                replies={[]}
                user={user}
                editingId={editingId}
                editText={editText}
                setEditText={setEditText}
                editError={editError}
                saving={saving}
                deleting={deleting}
                menuOpenId={menuOpenId}
                setMenuOpenId={setMenuOpenId}
                replyingToId={replyingToId}
                setReplyingToId={setReplyingToId}
                replyText={replyText}
                setReplyText={setReplyText}
                replyPosting={replyPosting}
                replyError={replyError}
                onStartEdit={onStartEdit}
                onCancelEdit={onCancelEdit}
                onSaveEdit={onSaveEdit}
                onDelete={onDelete}
                onReply={onReply}
                formatTime={formatTime}
                myAvatarUrl={myAvatarUrl}
                myAvatarInitial={myAvatarInitial}
                myAvatarGrad={myAvatarGrad}
                onReport={onReport}
                isReply
                likedSet={likedSet}
                likeCounts={likeCounts}
                onLike={onLike}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
